import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatAgeViewGroup, formatGroupCell } from 'utils/groupLabel';
import { formatUpdatedAt, groupByList, rankByPoints } from 'utils/meetPoints';
import style from './MeetPointsDashboardTable.module.css';
import { formatPoints } from 'utils/points';
import TeamColourDot from 'components/admin/TeamColourDot';

const MeetPointsDashboardTable = ({
  dashboardData,
  searchText,
  setSearchText,
  orderBy,
  setOrderBy,
  placeFilter,
  setPlaceFilter,
  liveStatus,
  teamWords = { team: 'Team' },
}) => {
  const [tableData, setTableData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'asc' });
  // A group is headed "U11 M" and an athlete's badge reads "11"; for a club that groups by year level they
  // read "Y6 M" and "Y6", and the view choice and the help say Year Group.
  const { groupingBasis, words } = useGroupLabels();

  // The points are the season's for this meet, by kind (docs/school-carnivals-points-slice-1.md in the
  // portal repo). The Season Best column shows only when the meet pays season-best points.
  const paysSeasonBest = Boolean(dashboardData?.paysSeasonBestPoints);
  // A school with houses (slice 4a): each athlete's house, by the house on their results at the meet.
  const hasHouses = Boolean(dashboardData?.houses);
  const columns = [
    { key: 'rank', label: 'Place' },
    { key: 'lastName', label: 'Name' },
    { key: 'ageGender', label: '', sortable: false },
    ...(hasHouses ? [{ key: 'houseName', label: teamWords.team, className: style.houseCell }] : []),
    { key: 'events', label: 'Events' },
    { key: 'firstPlaceCount', label: '1st Places' },
    { key: 'scoringPlaceCount', label: 'Places that score' },
    { key: 'placePoints', label: 'Place Points' },
    { key: 'takingPartPoints', label: 'Taking Part' },
    ...(paysSeasonBest ? [{ key: 'seasonBestPoints', label: 'Season Best' }] : []),
    { key: 'totalPoints', label: 'Total Points' },
  ];

  // The cells after the athlete's group, the same in both views: their house (a school with houses),
  // then their points.
  const renderPointsCells = (athlete) => (
    <>
      {hasHouses && (
        <td className={style.houseCell}>
          {athlete.houseName && (
            <span className={style.houseInner}>
              <TeamColourDot colour={athlete.houseColour} />
              {athlete.houseName}
            </span>
          )}
        </td>
      )}
      <td className={style.centered}>{athlete.events}</td>
      <td className={style.centered}>{athlete.firstPlaceCount}</td>
      <td className={style.centered}>{athlete.scoringPlaceCount}</td>
      <td className={style.centered}>{formatPoints(athlete.placePoints)}</td>
      <td className={style.centered}>{formatPoints(athlete.takingPartPoints)}</td>
      {paysSeasonBest && <td className={style.centered}>{formatPoints(athlete.seasonBestPoints)}</td>}
      <td className={style.totalPoints}>{formatPoints(athlete.totalPoints)}</td>
    </>
  );

  const getPlaceBadgeClass = (rank) => {
    if (rank === 1) return style.placeGold;
    if (rank === 2) return style.placeSilver;
    if (rank === 3) return style.placeBronze;
    if (rank <= 5) return style.placeTop5;
    return style.placeDefault;
  };

  const getGenderBadgeClass = (gender) => {
    return gender === 'F' ? style.genderFemale : style.genderMale;
  };

  // Each athlete with their house's name and colour, from the houses the dashboard carries.
  const athletes = useMemo(() => {
    const houseById = new Map((dashboardData?.houses || []).map((house) => [house.teamId, house]));
    return (dashboardData?.athletes || []).map((athlete) => {
      const house = houseById.get(athlete.teamId);
      return { ...athlete, houseName: house?.name || '', houseColour: house?.colour || null };
    });
  }, [dashboardData]);

  // The "age group" view's lists (points slice 6): a combined group ticked for every event an age ran is
  // one list, otherwise each age is its own (the server's listKey; utils/meetPoints.js, groupByList).
  const groupedByAgeGroup = useMemo(() => {
    if (!dashboardData?.athletes || orderBy !== 'ageGroup') return null;
    return groupByList(athletes);
  }, [dashboardData, athletes, orderBy]);

  // Calculate ranks on full dataset (before any filtering)
  // This ensures ranks are stable regardless of search/filter
  const rankedAthletes = useMemo(() => rankByPoints(athletes), [athletes]);

  // Apply search filter and sorting
  useEffect(() => {
    if (!rankedAthletes.length) {
      setTableData([]);
      return;
    }

    let filteredData = [...rankedAthletes];

    // Apply place filter
    if (placeFilter === 'top3') {
      filteredData = filteredData.filter((athlete) => athlete.rank <= 3);
    } else if (placeFilter === 'top5') {
      filteredData = filteredData.filter((athlete) => athlete.rank <= 5);
    }

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredData = filteredData.filter((athlete) => {
        const fullName = `${athlete.firstName} ${athlete.lastName}`.toLowerCase();
        return fullName.includes(searchLower);
      });
    }

    // Apply sorting based on sortConfig
    // Default sort is by rank (which is by points) when orderBy is 'points'
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle string comparisons
        if (['lastName', 'gender', 'houseName'].includes(sortConfig.key)) {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
          const comparison = aVal.localeCompare(bVal);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        // Numeric comparison
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }

    setTableData(filteredData);
  }, [rankedAthletes, searchText, sortConfig, placeFilter]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleOrderByChange = (e) => {
    setOrderBy(e.target.value);
  };

  const handlePlaceFilterChange = (e) => {
    setPlaceFilter(e.target.value);
  };

  const renderTableRows = () => {
    if (orderBy === 'ageGroup' && groupedByAgeGroup) {
      // Render grouped by age group
      return groupedByAgeGroup.flatMap((group, groupIndex) => {
        // Rank ALL athletes in the group first, so the ranks are the group's whatever the filters
        const athletesWithAgeGroupRank = rankByPoints(group.athletes).map((athlete) => ({
          ...athlete,
          ageGroupRank: athlete.rank,
        }));

        // Apply place filter within age group
        let visibleAthletes = athletesWithAgeGroupRank;
        if (placeFilter === 'top3') {
          visibleAthletes = visibleAthletes.filter((a) => a.ageGroupRank <= 3);
        } else if (placeFilter === 'top5') {
          visibleAthletes = visibleAthletes.filter((a) => a.ageGroupRank <= 5);
        }

        // Now filter by search text (after ranks are calculated)
        const filteredAthletes = visibleAthletes.filter((a) => {
          if (!searchText) return true;
          const fullName = `${a.firstName} ${a.lastName}`.toLowerCase();
          return fullName.includes(searchText.toLowerCase());
        });

        if (filteredAthletes.length === 0) return [];

        return [
          <tr key={`group-${groupIndex}`} className={`${style.ageGroupHeader} ${group.gender === 'F' ? style.ageGroupFemale : style.ageGroupMale}`}>
            <td colSpan={columns.length}>
              <div className={style.ageGroupHeaderContent}>
                {/* A combined list as Age View heads it, its name and range: "Year 11/12 (Y11-Y12) F" */}
                <span>{formatAgeViewGroup(groupingBasis, group.key, group.name)} {group.gender}</span>
              </div>
            </td>
          </tr>,
          ...filteredAthletes.map((athlete, index) => (
            <tr key={athlete.athleteId || `${groupIndex}-${index}`}>
              <td className={style.rank}>
                <span className={`${style.placeBadge} ${getPlaceBadgeClass(athlete.ageGroupRank)}`}>
                  {athlete.ageGroupRank}
                </span>
              </td>
              <td className={style.athleteName}>{athlete.firstName} {athlete.lastName}</td>
              <td className={style.badgeCell}>
                <span className={`${style.ageBadge}`}>{formatGroupCell(groupingBasis, athlete.age)}</span>
                <span className={`${style.genderBadge} ${getGenderBadgeClass(athlete.gender)}`}>{athlete.gender}</span>
              </td>
              {renderPointsCells(athlete)}
            </tr>
          )),
        ];
      });
    }

    // Render flat list ordered by points
    return tableData.map((athlete, index) => (
      <tr key={athlete.athleteId || index}>
        <td className={style.rank}>
          <span className={`${style.placeBadge} ${getPlaceBadgeClass(athlete.rank || index + 1)}`}>
            {athlete.rank || index + 1}
          </span>
        </td>
        <td className={style.athleteName}>{athlete.firstName} {athlete.lastName}</td>
        <td className={style.badgeCell}>
          <span className={style.ageBadge}>{formatGroupCell(groupingBasis, athlete.age)}</span>
          <span className={`${style.genderBadge} ${getGenderBadgeClass(athlete.gender)}`}>{athlete.gender}</span>
        </td>
        {renderPointsCells(athlete)}
      </tr>
    ));
  };

  return (
    <div className={style.tableCard}>
      {/* When the page was worked out, and the live badge. A school shows them at the top right of its
          House Totals card (Doug, 2026-10-24, H9); a club has no such card, so they stay here. */}
      {!hasHouses && (
        <div className={style.tableHeader}>
          <div className={style.headerInfo}>
            <div className={style.lastUpdated}>
              Last updated: {formatUpdatedAt(dashboardData?.calculatedAt)}
              {liveStatus === 'live' && (
                <span className={style.statusBadgeLive}>Live updates</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Bar and Filters */}
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search athletes by name..."
          value={searchText}
          onChange={handleSearchChange}
        />
        <div className={style.filterGroup}>
          <label htmlFor="orderBy" className={style.filterLabel}>
            View by:
          </label>
          <select
            id="orderBy"
            className={style.selectInput}
            value={orderBy}
            onChange={handleOrderByChange}
          >
            <option value="points">Total Points</option>
            <option value="ageGroup">{words.group}</option>
          </select>
        </div>
        <div className={style.filterGroup}>
          <label htmlFor="placeFilter" className={style.filterLabel}>
            Show:
          </label>
          <select
            id="placeFilter"
            className={style.selectInput}
            value={placeFilter}
            onChange={handlePlaceFilterChange}
          >
            <option value="top3">Top 3 Places</option>
            <option value="top5">Top 5 Places</option>
            <option value="all">All Places</option>
            <option value="allAthletes">All Athletes</option>
          </select>
        </div>
        {/* The counts, right-aligned where the help button was (Doug, 2026-10-24, H9: the help is in the
            page's top row now, MeetPointsHelp). */}
        <div className={style.statsInfo}>
          <div className={style.statItem}>
            Total Athletes: <span className={style.statValue}>{dashboardData?.totalAthletes || 0}</span>
          </div>
          <div className={style.statItem}>
            With Points: <span className={style.statValue}>{dashboardData?.athletesWithPoints || 0}</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={col.className}
                  onClick={col.sortable !== false && orderBy !== 'ageGroup' ? () => handleSort(col.key) : undefined}
                  style={{ cursor: col.sortable !== false && orderBy !== 'ageGroup' ? 'pointer' : 'default' }}
                >
                  {col.label}
                  {sortConfig.key === col.key && orderBy !== 'ageGroup' && (
                    <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(!dashboardData?.athletes || dashboardData.athletes.length === 0) ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No meet points data available
                </td>
              </tr>
            ) : (
              renderTableRows()
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

MeetPointsDashboardTable.propTypes = {
  dashboardData: PropTypes.shape({
    meetId: PropTypes.string,
    meetName: PropTypes.string,
    meetDate: PropTypes.string,
    athletes: PropTypes.arrayOf(
      PropTypes.shape({
        athleteId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        age: PropTypes.number,
        gender: PropTypes.string,
        events: PropTypes.number,
        firstPlaceCount: PropTypes.number,
        scoringPlaceCount: PropTypes.number,
        placePoints: PropTypes.number,
        takingPartPoints: PropTypes.number,
        seasonBestPoints: PropTypes.number,
        totalPoints: PropTypes.number,
        teamId: PropTypes.string,
        // The athlete's list in the group view (points slice 6): a combined group's key and name, or
        // their own number and null.
        listKey: PropTypes.string,
        listName: PropTypes.string,
      })
    ),
    // The House Totals card's rows (slice 4a); null for an organisation without houses.
    houses: PropTypes.arrayOf(PropTypes.object),
    totalAthletes: PropTypes.number,
    athletesWithPoints: PropTypes.number,
    calculatedAt: PropTypes.string,
    placePoints: PropTypes.arrayOf(PropTypes.number),
    eventTakingPartPoints: PropTypes.number,
    takingPartForEveryone: PropTypes.bool,
    meetTakingPartPoints: PropTypes.number,
    setSeasonBestPoints: PropTypes.number,
    equalSeasonBestPoints: PropTypes.number,
    beatSeasonBestPoints: PropTypes.number,
    excludeFirstSeasonBest: PropTypes.bool,
    paysSeasonBestPoints: PropTypes.bool,
    // How the meet scores, in the server's words: PointsExplanationList's sections.
    explanation: PropTypes.arrayOf(PropTypes.object),
  }),
  searchText: PropTypes.string,
  setSearchText: PropTypes.func.isRequired,
  orderBy: PropTypes.string,
  setOrderBy: PropTypes.func.isRequired,
  placeFilter: PropTypes.string,
  setPlaceFilter: PropTypes.func.isRequired,
  liveStatus: PropTypes.oneOf(['live']),
  // The organisation's words for a house (utils/teamLabel.js), for the House column.
  teamWords: PropTypes.shape({ team: PropTypes.string }),
};

export default MeetPointsDashboardTable;
